// src/app/profile/components/ProfileSectionLayout.tsx
'use client';

import React, { ReactNode, useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import ProfileHeader from './ProfileHeader';
import InlineBanner from '@/components/ui/inline-banner';
import PageCard from '@/components/ui/PageCard';
import { getBrowserBase } from '@/lib/apiBase';

const API_BASE = getBrowserBase();

interface ProfileSectionLayoutProps {
  title: string;
  children: ReactNode;
}

export default function ProfileSectionLayout({ title, children }: ProfileSectionLayoutProps) {
  useEffect(() => {
    try {
      const el = document.getElementById('profile-section-title');
      // Avoid layout jank from auto-focus scrolling the page a few pixels
      (el as any)?.focus?.({ preventScroll: true });
    } catch {}
  }, [title]);
  const [netErr, setNetErr] = useState<{ code: string; message?: string } | null>(null);
  // Note: an empty API_BASE is a valid configuration — it means "use same-origin
  // requests and let the Next.js `/api/:path*` rewrite proxy them to API_BASE_HTTP".
  // Don't flag it as invalid here.

  useEffect(() => {
    function onNetworkFailed(e: any) {
      const detail = e?.detail || {};
      setNetErr({ code: 'network_failed', message: detail?.error?.detail || detail?.error?.message });
    }
    try {
      if (typeof window !== 'undefined') {
        window.addEventListener('api:network_failed', onNetworkFailed as any);
        return () => window.removeEventListener('api:network_failed', onNetworkFailed as any);
      }
    } catch {}
  }, []);
  const pathname = usePathname();

  const hasTitle = !!(title && title.trim());

  return (
    <div className={`px-2 md:px-3 ${hasTitle ? 'py-2' : 'pt-0 pb-2'}`}>
      {netErr?.code === 'network_failed' && (
        <InlineBanner variant="error" className="space-y-1">
          <div>
            Can&apos;t reach API at {API_BASE || '(empty)'}. The API may be temporarily unavailable — please try again in a moment.
          </div>
          {process.env.NODE_ENV === 'development' && (
            <button
              type="button"
              className="underline text-xs"
              onClick={() => {
                try { window.open(`${API_BASE}/api/health`, '_blank'); } catch {}
              }}
            >
              Open health check
            </button>
          )}
        </InlineBanner>
      )}
      {/* Render header only when a non-empty title is provided */}
      {hasTitle ? <ProfileHeader title={title} /> : null}
      {/* Tabs removed per design — use side nav only */}
      {/* aria-live region for async announcements from section forms */}
      <div className="sr-only" aria-live="polite" />
      {/* No global wrapper here — each page/component owns its container to avoid double boxes */}
      <div className={hasTitle ? 'py-3' : 'pt-0 pb-3'}>{children}</div>
    </div>
  );
}
